import { useOrchestratorApi } from '@/composables/orchestrator/useOrchestratorApi'
import type { PostWorkflow, Workflow } from '@/composables/orchestrator/types'
import type { Mode } from '@/utils/api-urls'
import decode, { type JwtPayload } from 'jwt-decode'
import { readonly, ref } from 'vue'

export type TokenDecoded = JwtPayload & {
    partyId: string
    workspaceId: string
    environment: Mode
    workflowDefinitionId: string
    additional: Record<string, unknown>
    source: {
        userName: string
        userId: string
        sourceType: string
    }
    lang?: string
    workflowId?: string
    version: number
    engineType?: 'ORCHESTRATOR' | 'WORKFLOW_EXECUTOR'
}

const token = ref<string>()
const decodedToken = ref<TokenDecoded>()
const workflowId = ref<string>()
const partyId = ref<string>()
const mode = ref<Mode>('staging')

const localeHeader = ref<string | undefined>()

export const setLocaleHeader = (locale?: string) => {
    localeHeader.value = locale
}

export const useWorkflow = () => {

    const validateToken = async (token: string) => {
        const { data } = await useOrchestratorApi().post<never>(
            '/token/validate',
            { token },
        )

        return data
    }

    const getCurrentWorkflow = async (): Promise<PostWorkflow | undefined> => {
        const { data } = await useOrchestratorApi().post<PostWorkflow | undefined>(
            '/workflows',
            null,
            {
                headers: {
                    Authorization: `Bearer ${token.value}`,
                },
            },
        )

        if (data) {
            setWorkflowId(data.id)
            setPartyId(data.caseId)
        }

        return data
    }

    const resumeWorkflow = async (taskId: string, taskType: string, language?: string): Promise<Workflow | void> => {
        const response = await useOrchestratorApi().post(
            `/workflows/${workflowId.value}/resume`,
            {
                language,
                taskId,
                taskType,
                workflowId: workflowId.value,
            },
            {
                headers: {
                    Authorization: `Bearer ${token.value}`,
                },
            },
        )

        if (response?.data) {
            setLocaleHeader(language)
        }
    }

    const setToken = (value?: string): TokenDecoded => {
        decodedToken.value = decode<TokenDecoded>(value ?? '')
        token.value = value
        const { environment, partyId } = decodedToken.value
        setMode(environment)
        setPartyId(partyId)
        if (decodedToken.value.workflowId) {
            setWorkflowId(decodedToken.value.workflowId)
        }

        return decodedToken.value
    }

    const clearToken = async () => {
        token.value = undefined
        decodedToken.value = undefined
    }

    const setWorkflowId = (value: string) => {
        workflowId.value = value
    }

    const setPartyId = (value: string) => {
        partyId.value = value
    }

    const setMode = (value: Mode) => {
        mode.value = value
    }

    return {
        validateToken,
        token: readonly(token),
        decodedToken: readonly(decodedToken),
        workflowId: readonly(workflowId),
        mode: readonly(mode),
        localeHeader: readonly(localeHeader),
        setToken,
        setWorkflowId,
        setMode,
        getCurrentWorkflow,
        clearToken, // used in tests
        resumeWorkflow,
    }
}
